import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Divider,
  FormControl,
  Autocomplete,
  TextField,
  LinearProgress,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Delete as DeleteIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, RemoveCircle as RemoveCircleIcon, Block as BlockIcon, WarningAmber as WarningIcon } from "@mui/icons-material";
import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import Papa from "papaparse";
import bulkCreateContacts, { syncContactsToList } from "api/contacts/bulkCreateContact";
import { getMailLists } from "api/canmail/CANmailAPI";

const REQUIRED_HEADERS = ["firstname", "lastname", "workemail"];
const OPTIONAL_HEADERS = ["personalemail", "primaryphone", "secondaryphone", "address", "notes"];
const ALL_HEADERS = [...REQUIRED_HEADERS, ...OPTIONAL_HEADERS];
// How many skipped rows to enumerate in the warning before collapsing to "…and N more".
const MAX_LISTED_INVALID = 8;

function BulkContactUploadModal({ open, onClose, companies = [], onContactsRefresh, mode = "import" }) {
  const isSync = mode === "sync";
  const [uppyInstance, setUppyInstance] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  // Per-row problems that are SKIPPED (not blocking): { index, rowNum, missing: string[] }.
  const [invalidRows, setInvalidRows] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [mailLists, setMailLists] = useState([]);
  const [selectedMailList, setSelectedMailList] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadResults, setUploadResults] = useState({ success: [], failed: [] });
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (open) {

      // Load all mail lists (mirrors the List Manager page, which shows all lists with no filter)
      (async () => {
        try {
          const lists = await getMailLists();
          setMailLists(Array.isArray(lists) ? lists : []);
        } catch (error) {
          console.error("Error loading mailing lists:", error);
          setMailLists([]);
        }
      })();

      const uppy = new Uppy({
        restrictions: {
          maxNumberOfFiles: 1,
          allowedFileTypes: [".csv"],
        },
        autoProceed: false,
      });

      uppy.on("file-added", (file) => {
        if (file.extension !== "csv") {
          uppy.removeFile(file.id);
          return;
        }
        
        // Parse CSV file
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const csvText = e.target.result;
            const result = Papa.parse(csvText, { header: true, skipEmptyLines: true });
            
            if (result.errors.length > 0) {
              setValidationErrors([`CSV parsing error: ${result.errors[0].message}`]);
              setInvalidRows([]);
              setIsValid(false);
              return;
            }

            const data = result.data;
            const { errors, invalidRows: badRows } = analyzeCsvData(data);

            setValidationErrors(errors);
            setInvalidRows(badRows);
            // Only file-level problems (empty file / missing headers) block the import.
            // Rows missing required values are skipped, not blocking.
            setIsValid(errors.length === 0);
            setCsvData(data);
          } catch (error) {
            setValidationErrors([`Error reading CSV file: ${error.message}`]);
            setInvalidRows([]);
            setIsValid(false);
          }
        };
        reader.readAsText(file.data);
      });

      uppy.on("file-removed", () => {
        setCsvData([]);
        setValidationErrors([]);
        setInvalidRows([]);
        setIsValid(false);
      });

      setUppyInstance(uppy);
      return () => {
        uppy.close();
        setCsvData([]);
        setValidationErrors([]);
        setInvalidRows([]);
        setIsValid(false);
        setSelectedCompany("");
        setSelectedMailList("");
        setIsUploading(false);
        setUploadProgress({ current: 0, total: 0 });
        setUploadResults({ success: [], failed: [] });
        setIsCompleted(false);
      };
    }
  }, [open]);

  function parseCountsFromMessage(msg) {
  const text = (msg || "").toLowerCase();
  const num = (re) => {
    const m = text.match(re);
    return m ? parseInt(m[1], 10) || 0 : 0;
  };
  return {
    created: num(/created\s*:\s*(\d+)/i),
    skipped: num(/skipped\s*:\s*(\d+)/i),
    failed:  num(/failed\s*:\s*(\d+)/i),
    addedToList: num(/addedtolist\s*:\s*(\d+)/i),
    removed: num(/removed\s*:\s*(\d+)/i),
    // Contacts skipped because they previously unsubscribed from the target list (not re-added, CASL).
    unsubscribed: num(/unsubscribed\s*:\s*(\d+)/i),
    // Contacts skipped because their address previously hard-bounced and was removed by list cleaning.
    hardBounced: num(/hardbounced\s*:\s*(\d+)/i),
    allSkipped: /all\s+skipped/i.test(text)
  };
}


  // Splits parse output into (a) file-level BLOCKING errors that prevent any
  // import — empty file, or no required column at all — and (b) per-row problems
  // (missing required values) that are SKIPPED rather than blocking the upload.
  const analyzeCsvData = (data) => {
    const errors = [];
    const invalid = [];

    if (data.length === 0) {
      errors.push("CSV file is empty");
      return { errors, invalidRows: invalid };
    }

    // Check headers
    const headers = Object.keys(data[0] || {});
    const missingRequired = REQUIRED_HEADERS.filter(header =>
      !headers.some(h => h.toLowerCase() === header.toLowerCase())
    );

    if (missingRequired.length > 0) {
      // A required column is entirely absent, so nothing can be imported — block.
      errors.push(`Missing required headers: ${missingRequired.join(", ")}`);
      return { errors, invalidRows: invalid };
    }

    // Check data rows — a row missing any required value is flagged for skipping.
    data.forEach((row, index) => {
      const missing = [];
      REQUIRED_HEADERS.forEach(header => {
        const headerKey = headers.find(h => h.toLowerCase() === header.toLowerCase());
        if (!headerKey || !row[headerKey] || row[headerKey].trim() === "") {
          missing.push(header);
        }
      });
      if (missing.length > 0) {
        invalid.push({ index, rowNum: index + 1, missing });
      }
    });

    return { errors, invalidRows: invalid };
  };

  const isFormValid = () => {
    // Need at least one VALID row; invalid rows are skipped, not blocking.
    const validCount = csvData.length - invalidRows.length;
    const base = isValid && validCount > 0 && selectedCompany;
    if (isSync) {
      // Sync targets a specific list, so the list is required.
      return base && selectedMailList;
    }
    return base;
  };

  const handleRemoveRow = (index) => {
    const newData = csvData.filter((_, i) => i !== index);
    setCsvData(newData);

    if (newData.length > 0) {
      const { errors, invalidRows: badRows } = analyzeCsvData(newData);
      setValidationErrors(errors);
      setInvalidRows(badRows);
      setIsValid(errors.length === 0);
    } else {
      setValidationErrors([]);
      setInvalidRows([]);
      setIsValid(false);
    }
  };

  const mapCsvToContact = (csvRow) => {
    const contact = {
      Id: "", // Empty for new contacts
      FirstName: csvRow.firstname || csvRow.firstName || "",
      LastName: csvRow.lastname || csvRow.lastName || "",
      Customers: [selectedCompany], // Array of company IDs
      WorkEmail: csvRow.workemail || csvRow.workEmail || csvRow.emailwork || "",
      PersonalEmail: csvRow.personalemail || csvRow.personalEmail || csvRow.emailpersonal || "",
      OtherEmail: "", // Empty for now
      ContactTypes: ["2"], // Array of contact type IDs (default to 'Alternate' type)
      WorkPhone: csvRow.primaryphone || csvRow.primaryPhone || csvRow.phonework || "",
      WorkPhoneDialCode: "1", // Default dial code
      WorkPhoneCountryCode: "ca", // Default country code
      WorkPhoneExtension: "", // Empty for now
      CellPhone: csvRow.secondaryphone || csvRow.secondaryPhone || csvRow.phonemobile || "",
      CellPhoneDialCode: "1", // Default dial code
      CellPhoneCountryCode: "ca", // Default country code
      CellPhoneExtension: "", // Empty for now
      HomePhone: "", // Empty for now
      HomePhoneDialCode: "", // Empty for now
      HomePhoneCountryCode: "", // Empty for now
      HomePhoneExtension: "", // Empty for now
      Note: csvRow.notes || csvRow.note || "",
      Addresses: [ // Empty address objects as required by API
        {
          address_id: "",
          type: "",
          street: ", ",
          city: "",
          province: "",
          country: "",
          postalCode: "",
          placeId: "",
          lat: "",
          lng: ""
        },
        {
          address_id: "",
          type: "",
          street: ", ",
          city: "",
          province: "",
          country: "",
          postalCode: "",
          placeId: "",
          lat: "",
          lng: ""
        },
        {
          address_id: "",
          type: "",
          street: ", ",
          city: "",
          province: "",
          country: "",
          postalCode: "",
          placeId: "",
          lat: "",
          lng: ""
        }
      ],
      Languages: [], // Empty array as required by API
      Image: { // Empty image object as required by API
        file: "",
        name: "",
        preview: ""
      }
    };
    
    return contact;
  };

  // Treats a single per-item value and returns a normalized status object
function normalizeItemStatus(value) {
  // Allowed shapes:
  // 1) "success" | "failure" | "skipped"
  // 2) true | false
  // 3) { Status: "Skipped", Reason: "Duplicate ..." }
  // 4) { success: true/false, error?: string, message?: string }
  if (value === true || value === "success") {
    return { status: "success" };
  }
  if (value === false || value === "failure") {
    return { status: "failure", error: "Failed" };
  }

  if (typeof value === "object" && value !== null) {
    const lowerStatus = (value.Status || value.status || "").toString().toLowerCase();
    if (lowerStatus === "skipped") {
      return { status: "skipped", error: value.Reason || value.reason || "Skipped" };
    }
    if (typeof value.success === "boolean") {
      return value.success
        ? { status: "success", message: value.message }
        : { status: "failure", error: value.error || value.message || "Failed" };
    }
    // Fallback: if the object looks like a success payload from create
    if ("Id" in value || "id" in value) {
      return { status: "success", message: "Created" };
    }
  }

  // Fallback to failure with a readable value
  return { status: "failure", error: typeof value === "string" ? value : "Failed" };
}

// Builds { success:[], failed:[] } using server Data map and original contacts
function buildResultsFromServerMap(serverMap, contacts, csvRows) {
  const results = { success: [], failed: [] };
  // serverMap = { "0": "success", "1": "failure", "2": { Status:"Skipped", Reason:"Duplicate" }, ... }

  // Guard: if map is empty or not an object, return null to let caller decide a fallback.
  if (!serverMap || typeof serverMap !== "object" || Array.isArray(serverMap)) return null;

  Object.entries(serverMap).forEach(([k, v]) => {
    const index = Number(k);
    const norm = normalizeItemStatus(v);
    const contact = contacts[index];
    const originalCsv = csvRows[index];

    if (norm.status === "success") {
      results.success.push({
        row: index + 1,
        contact,
        response: { message: norm.message || "Created successfully" }
      });
    } else if (norm.status === "skipped") {
      results.failed.push({
        row: index + 1,
        contact,
        error: norm.error || "Skipped"
      });
    } else {
      results.failed.push({
        row: index + 1,
        contact,
        error: norm.error || "Failed"
      });
    }
  });

  return results;
}

const handleUpload = async () => {
  if (!selectedCompany) {
    alert("Please select a company first");
    return;
  }

  // Only submit rows that pass per-row validation; the rest are skipped and
  // reported. The backend never sees the invalid rows (and it does no name/email
  // validation of its own, so sending blank rows would create junk contacts).
  const invalidIndexSet = new Set(invalidRows.map((r) => r.index));
  const validRows = csvData.filter((_, i) => !invalidIndexSet.has(i));
  const invalidSkipped = csvData.length - validRows.length;

  if (validRows.length === 0) {
    // Button is normally disabled in this case; guard defensively.
    return;
  }

  const contactsToCreate = validRows.map(mapCsvToContact);

  setIsUploading(true);
  setIsCompleted(false);
  setUploadProgress({ current: 0, total: validRows.length });
  setUploadResults({ success: [], failed: [], summary: { created: 0, skipped: 0, failed: 0, invalidSkipped, overallSuccess: false } });

  try {
    setUploadProgress({ current: 0, total: contactsToCreate.length });

    const response = isSync
      ? await syncContactsToList(contactsToCreate, selectedMailList)
      : await bulkCreateContacts(contactsToCreate, selectedMailList || 0);

    const overallSuccess = !!(response && response.Success);
    const counts = parseCountsFromMessage(response && response.Message);

    // Build results object for UI.
    // We still don't have per-row details from the server, so we show totals.
    const results = {
      success: [],         // we won't fake per-row successes
      failed: [],          // we will only fill if overall failure
      summary: {
        created: counts.created,
        skipped: counts.skipped,
        failed: counts.failed,
        addedToList: counts.addedToList,
        removed: counts.removed,
        unsubscribed: counts.unsubscribed,
        hardBounced: counts.hardBounced,
        invalidSkipped,   // rows we skipped client-side for missing required data
        overallSuccess
      }
    };

    if (!overallSuccess) {
      const reason = (response && (response.Message || response.Error || response.statusText)) || "Bulk create failed";
      // Without a per-row map, mark all failed for consistency (existing behavior)
      results.failed = contactsToCreate.map((c, i) => ({
        row: i + 1,
        contact: c,
        error: reason
      }));
    }

    setUploadResults(results);
    setUploadProgress({ current: validRows.length, total: validRows.length });

    // Refresh list if anything actually changed (created, linked, added to list, or removed via sync)
    if (
      overallSuccess &&
      (counts.created > 0 || counts.skipped > 0 || counts.addedToList > 0 || counts.removed > 0) &&
      typeof onContactsRefresh === "function"
    ) {
      onContactsRefresh();
    }
  } catch (error) {
    console.error("Bulk upload error:", error);
    const reason = (error && error.message) || "Network error occurred";
    setUploadResults({
      success: [],
      failed: validRows.map((row, index) => ({
        row: index + 1,
        contact: row,
        error: reason
      })),
      summary: { created: 0, skipped: 0, failed: validRows.length, invalidSkipped, overallSuccess: false }
    });
  } finally {
    setIsUploading(false);
    setIsCompleted(true);
  }
};


  const handleCancel = () => {
    setCsvData([]);
    setValidationErrors([]);
    setInvalidRows([]);
    setIsValid(false);
    setSelectedCompany("");
    setSelectedMailList("");
    setIsUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    setUploadResults({ success: [], failed: [] });
    setIsCompleted(false);
    onClose();
  };

  const invalidCount = invalidRows.length;
  const validCount = csvData.length - invalidCount;
  // index -> missing[] for quick per-row lookup while rendering the preview.
  const invalidRowMap = new Map(invalidRows.map((r) => [r.index, r.missing]));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{isSync ? "Sync Contacts to Mailing List" : "Bulk Import Contacts"}</DialogTitle>
             <DialogContent>
                   {/* Show upload form only when not uploading AND not completed */}
          {!isUploading && !isCompleted && (
           <>
             {isSync && (
               <Alert severity="info" sx={{ mb: 3 }}>
                 <Typography variant="body2">
                   Syncing makes the selected mailing list match your CSV <strong>exactly</strong>.
                   Contacts in the CSV are added (and created if they don&apos;t already exist), and any
                   contact currently on the list that is <strong>not</strong> in your CSV is removed from
                   the list. This does not delete the contacts themselves — only their membership in this list.
                   Contacts who previously <strong>unsubscribed</strong> from this list, or whose address was
                   removed for <strong>hard-bouncing</strong>, are automatically skipped and will not be
                   re-added, even if they appear in your CSV.
                 </Typography>
               </Alert>
             )}

             <Box sx={{ mb: 3 }}>
               <Typography variant="body2" color="text.secondary" paragraph>
                 Upload a CSV file with the following format. Rows missing a required
                 field are skipped (and reported), so a few bad rows won&apos;t block the rest.
               </Typography>

               <Box sx={{ mb: 2 }}>
                 <Typography variant="subtitle2" gutterBottom>
                   Required Headers:
                 </Typography>
                 <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                   {REQUIRED_HEADERS.map(header => (
                     <Chip key={header} label={header} color="primary" size="small" />
                   ))}
                 </Box>
                 
                 <Typography variant="subtitle2" gutterBottom>
                   Optional Headers:
                 </Typography>
                 <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                   {OPTIONAL_HEADERS.map(header => (
                     <Chip key={header} label={header} variant="outlined" size="small" />
                   ))}
                 </Box>
               </Box>
             </Box>

             <Box sx={{ mb: 3 }}>
               <Typography variant="h6" gutterBottom>
                 Company Assignment
               </Typography>
               <Typography variant="body2" color="text.secondary" paragraph>
                 All imported contacts will be assigned to the selected company.
               </Typography>
               <FormControl fullWidth sx={{ maxWidth: 400 }}>
                  <Autocomplete
                    options={companies}
                    getOptionLabel={(option) => option.Name || option.name || ''}
                    isOptionEqualToValue={(option, value) =>
                      (option.id || option.Id) === (value.id || value.Id)
                    }
                    value={companies.find((c) => (c.id || c.Id) === selectedCompany) || null}
                    onChange={(event, newValue) =>
                      setSelectedCompany(newValue ? (newValue.id || newValue.Id) : '')
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company"
                        required
                        error={csvData.length > 0 && !selectedCompany}
                      />
                    )}
                  />
                  {csvData.length > 0 && !selectedCompany && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      Company selection is required
                    </Typography>
                  )}
               </FormControl>
             </Box>

             <Box sx={{ mb: 3 }}>
               <Typography variant="h6" gutterBottom>
                 {isSync ? "Mailing List (required)" : "Add to Mailing List (optional)"}
               </Typography>
               <Typography variant="body2" color="text.secondary" paragraph>
                 {isSync
                   ? "Select the mailing list to sync. Its members will be made to match your CSV exactly."
                   : "Optionally add all imported contacts to a mailing list."}
               </Typography>
               <FormControl fullWidth sx={{ maxWidth: 400 }}>
                 <Autocomplete
                   options={mailLists}
                   getOptionLabel={(option) => option.list_name || ''}
                   isOptionEqualToValue={(option, value) => option.list_id === value.list_id}
                   value={mailLists.find((l) => l.list_id === selectedMailList) || null}
                   onChange={(event, newValue) =>
                     setSelectedMailList(newValue ? newValue.list_id : '')
                   }
                   renderInput={(params) => (
                     <TextField
                       {...params}
                       label="Mailing List"
                       placeholder={isSync ? "Select a list" : "— None —"}
                       required={isSync}
                       error={isSync && csvData.length > 0 && !selectedMailList}
                     />
                   )}
                 />
                 {isSync && csvData.length > 0 && !selectedMailList && (
                   <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                     A mailing list is required for sync
                   </Typography>
                 )}
                 {!isSync && selectedMailList && (
                   <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                     Contacts who previously unsubscribed from this list, or whose address hard-bounced, are skipped and will not be re-added.
                   </Typography>
                 )}
               </FormControl>

               {isSync && selectedMailList && (
                 <Box sx={{ mt: 2, maxWidth: 560 }}>
                   <Alert severity="warning">
                     Contacts on{" "}
                     <strong>
                       {mailLists.find((l) => l.list_id === selectedMailList)?.list_name || "this list"}
                     </strong>{" "}
                     that are not in this CSV will be removed from the list. This can&apos;t be undone.
                   </Alert>
                 </Box>
               )}
             </Box>

             <Divider sx={{ mb: 3 }} />

             <Box sx={{ mb: 3 }}>
               <Typography variant="h6" gutterBottom>
                 Upload CSV File
               </Typography>
               {uppyInstance && (
                 <Dashboard
                   uppy={uppyInstance}
                   hideUploadButton
                   hideProgressAfterFinish
                   height={200}
                 />
               )}
             </Box>

             {validationErrors.length > 0 && (
               <Box sx={{ mb: 3 }}>
                 <Alert severity="error">
                   <Typography variant="subtitle2" gutterBottom>
                     Validation Errors:
                   </Typography>
                   <ul style={{ margin: 0, paddingLeft: "20px" }}>
                     {validationErrors.map((error, index) => (
                       <li key={index}>
                         <Typography variant="body2">{error}</Typography>
                       </li>
                     ))}
                   </ul>
                 </Alert>
               </Box>
             )}

             {invalidRows.length > 0 && (
               <Box sx={{ mb: 3 }}>
                 <Alert severity="warning">
                   <Typography variant="subtitle2" gutterBottom>
                     {validCount > 0
                       ? `${invalidCount} of ${csvData.length} row${csvData.length === 1 ? "" : "s"} will be skipped`
                       : "No valid rows to import"}
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 1 }}>
                     {validCount > 0 ? (
                       <>
                         These rows are missing required data ({REQUIRED_HEADERS.join(", ")}) and
                         will not be {isSync ? "synced" : "imported"}. The remaining {validCount} valid
                         row{validCount === 1 ? "" : "s"} will be {isSync ? "synced" : "imported"} normally.
                       </>
                     ) : (
                       <>
                         Every row is missing at least one required field
                         ({REQUIRED_HEADERS.join(", ")}), so there is nothing to
                         {isSync ? " sync" : " import"}. Fix the file and re-upload.
                       </>
                     )}
                   </Typography>
                   {isSync && validCount > 0 && (
                     <Typography variant="body2" sx={{ mb: 1 }}>
                       Note: because sync makes the list match your CSV exactly, a skipped row that
                       matches an existing member will not keep that member on the list.
                     </Typography>
                   )}
                   <ul style={{ margin: 0, paddingLeft: "20px" }}>
                     {invalidRows.slice(0, MAX_LISTED_INVALID).map((r) => (
                       <li key={r.index}>
                         <Typography variant="body2">
                           Row {r.rowNum}: missing {r.missing.join(", ")}
                         </Typography>
                       </li>
                     ))}
                   </ul>
                   {invalidRows.length > MAX_LISTED_INVALID && (
                     <Typography variant="body2" sx={{ mt: 0.5 }}>
                       …and {invalidRows.length - MAX_LISTED_INVALID} more.
                     </Typography>
                   )}
                 </Alert>
               </Box>
             )}

             {csvData.length > 0 && (
               <Box sx={{ mb: 3 }}>
                 <Typography variant="h6" gutterBottom>
                   Preview ({csvData.length} contacts{invalidCount > 0 ? `, ${invalidCount} will be skipped` : ""})
                 </Typography>
                 {selectedCompany && (
                   <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                     All contacts will be assigned to: <strong>{companies.find(c => (c.id || c.Id) === selectedCompany)?.Name || companies.find(c => (c.id || c.Id) === selectedCompany)?.name}</strong>
                   </Typography>
                 )}
                 <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                   <Table stickyHeader size="small">
                     <TableHead>
                       <TableRow>
                         <TableCell>Row</TableCell>
                         <TableCell>Actions</TableCell>
                         {ALL_HEADERS.map(header => (
                           <TableCell key={header}>{header}</TableCell>
                         ))}
                       </TableRow>
                     </TableHead>
                     <TableBody>
                       {csvData.map((row, index) => {
                         const missing = invalidRowMap.get(index);
                         const isInvalid = !!missing;
                         return (
                         <TableRow key={index} sx={isInvalid ? { bgcolor: "action.hover" } : undefined}>
                           <TableCell
                             sx={{
                               fontWeight: isInvalid ? 700 : 400,
                               color: isInvalid ? "warning.main" : "inherit",
                             }}
                           >
                             {index + 1}
                           </TableCell>
                           <TableCell>
                             <Box sx={{ display: "flex", alignItems: "center" }}>
                               {isInvalid && (
                                 <Tooltip title={`Will be skipped — missing ${missing.join(", ")}`}>
                                   <WarningIcon color="warning" fontSize="small" sx={{ mr: 0.5 }} />
                                 </Tooltip>
                               )}
                               <IconButton
                                 size="small"
                                 onClick={() => handleRemoveRow(index)}
                                 color="error"
                               >
                                 <DeleteIcon />
                               </IconButton>
                             </Box>
                           </TableCell>
                           {ALL_HEADERS.map(header => {
                             const headerKey = Object.keys(row).find(h => 
                               h.toLowerCase() === header.toLowerCase()
                             );
                             const value = headerKey ? row[headerKey] : "";
                             return (
                               <TableCell key={header}>
                                 <Typography
                                   variant="body2"
                                   sx={{
                                     maxWidth: 150,
                                     overflow: "hidden",
                                     textOverflow: "ellipsis",
                                     whiteSpace: "nowrap"
                                   }}
                                 >
                                   {value || "-"}
                                 </Typography>
                               </TableCell>
                             );
                           })}
                         </TableRow>
                         );
                       })}
                     </TableBody>
                   </Table>
                 </TableContainer>
               </Box>
             )}
           </>
         )}

                   {/* Upload Progress - Only visible when uploading AND not completed */}
          {isUploading && !isCompleted && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {isSync ? "Syncing Contacts..." : "Uploading Contacts..."}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(uploadProgress.current / uploadProgress.total) * 100} 
                  sx={{ flexGrow: 1 }}
                />
                <Typography variant="body2">
                  {uploadProgress.current} / {uploadProgress.total}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Upload Results - Only visible when completed */}
          {isCompleted && (
            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="body2" color="success.main">
                  {uploadResults.summary ? uploadResults.summary.created : uploadResults.success.length} created
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {uploadResults.summary ? uploadResults.summary.skipped : 0} skipped
                </Typography>
              </Box>

              {uploadResults.summary && uploadResults.summary.invalidSkipped > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="body2" color="warning.main">
                    {uploadResults.summary.invalidSkipped} skipped (invalid data)
                  </Typography>
                </Box>
              )}

              {((uploadResults.summary && uploadResults.summary.failed) || uploadResults.failed.length > 0) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon color="error" />
                  <Typography variant="body2" color="error.main">
                    {uploadResults.summary ? uploadResults.summary.failed : uploadResults.failed.length} failed
                  </Typography>
                </Box>
              )}

              {uploadResults.summary && uploadResults.summary.addedToList > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="body2" color="success.main">
                    {uploadResults.summary.addedToList} added to list
                  </Typography>
                </Box>
              )}

              {isSync && uploadResults.summary && uploadResults.summary.removed > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RemoveCircleIcon color="warning" />
                  <Typography variant="body2" color="warning.main">
                    {uploadResults.summary.removed} removed from list
                  </Typography>
                </Box>
              )}

              {uploadResults.summary && uploadResults.summary.unsubscribed > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BlockIcon color="warning" />
                  <Typography variant="body2" color="warning.main">
                    {uploadResults.summary.unsubscribed} skipped (unsubscribed)
                  </Typography>
                </Box>
              )}

              {uploadResults.summary && uploadResults.summary.hardBounced > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BlockIcon color="warning" />
                  <Typography variant="body2" color="warning.main">
                    {uploadResults.summary.hardBounced} skipped (hard bounced)
                  </Typography>
                </Box>
              )}
            </Box>
          )}
       </DialogContent>
      <DialogActions sx={{ mx: 2, mb: 2 }}>
        {!isCompleted && (
          <Button onClick={handleCancel} color="error">
            Cancel
          </Button>
        )}
        {!isCompleted ? (
          <Button 
            onClick={handleUpload} 
            variant="contained"
            disabled={!isFormValid() || isUploading}
            startIcon={isUploading ? <CircularProgress size={16} /> : null}
          >
            {isSync
              ? (isUploading ? 'Syncing...' : `Sync (${validCount} contacts)`)
              : (isUploading ? 'Uploading...' : `Upload (${validCount} contacts)`)}
          </Button>
        ) : (
          <Button 
            onClick={onClose} 
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BulkContactUploadModal;
